import { useState, useEffect, Suspense } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePartnerCounts } from '../hooks/usePartnerCounts';
import ErrorBoundary from './ErrorBoundary';
import SalesHeutePanel from './panels/SalesHeutePanel';
import SalesPanel from './panels/SalesPanel';
import SalesChatPanel from './panels/SalesChatPanel';
import SalesMessagesPanel from './panels/SalesMessagesPanel';
import SalesMaterialsPanel from './panels/SalesMaterialsPanel';
import SalesLeitfadenPanel from './panels/SalesLeitfadenPanel';
import SalesZielePanel from './panels/SalesZielePanel';
import BusinessOverviewPanel from './panels/BusinessOverviewPanel';
import FeedbackPanel from './panels/FeedbackPanel';
import { pickArea } from './AreaPicker';
import WasWirktAufMich from './WasWirktAufMich';
import StageSharesInbox from './StageSharing';

// ============================================================================
// Der Sales-Bereich — die Zusammenarbeitsflaeche zwischen einem Sales-Partner
// und Rafael. Kein FlexLayout, kein Tool Hub, keine Build-Knoepfe: eine Zeile
// Reiter, daneben der Chat, und darunter das, was ein Vertriebspartner
// wirklich braucht.
//
// Er begann als Nebenausgang ("Kontakt-Log, Business-Uebersicht, Frage an
// Rafael — sonst nichts") und hatte damit eine Luecke, die genau das
// verhinderte, wofuer er gedacht war: keinen Nachrichtenkanal. Rafaels acht
// Nachrichten vom Juli lagen im Postfach des Arbeitsbereichs, wo ein
// Sales-Partner nie hinkommt — Absprachen liefen weiter per Telefon.
//
// Seit 02.09.2026 ist er die Arbeitsflaeche: Tagesansicht, gemeinsamer
// Trichter mit Verlauf und Ablage, Nachrichten in beide Richtungen, die
// eigenen Unterlagen, der Gespraechsleitfaden — und der Chat als Spalte
// daneben statt als siebter Reiter (Rafael, 02.09.: "der Agent mit dem Chat,
// links oder rechts, wie ueberall anders auch").
// ============================================================================

type Section = 'heute' | 'ziele' | 'kunden' | 'nachrichten' | 'unterlagen' | 'leitfaden' | 'uebersicht' | 'feedback';

const SECTIONS: { value: Section; label: string }[] = [
  { value: 'heute', label: 'Heute' },
  { value: 'ziele', label: 'Ziele' },
  { value: 'kunden', label: 'Kunden' },
  { value: 'nachrichten', label: 'Nachrichten' },
  { value: 'unterlagen', label: 'Unterlagen' },
  { value: 'leitfaden', label: 'Leitfaden' },
  { value: 'uebersicht', label: 'Business-Übersicht' },
  { value: 'feedback', label: 'Feedback' },
];

/** Wie breit die Chat-Spalte steht. Merkt sich der Browser pro Person. */
const CHAT_BREITE_KEY = 'sales-chat-offen';

export default function SalesLanding() {
  const { user, logout } = useAuth();
  const [section, setSection] = useState<Section>('heute');
  const [wasWirktOffen, setWasWirktOffen] = useState(false);
  const counts = usePartnerCounts();

  // Der Chat bleibt offen, wenn er offen war. Wer ihn zuklappt, will ihn nicht
  // bei jedem Seitenwechsel wiedersehen.
  const [chatOffen, setChatOffen] = useState<boolean>(() => {
    try { return localStorage.getItem(CHAT_BREITE_KEY) !== 'zu'; } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem(CHAT_BREITE_KEY, chatOffen ? 'offen' : 'zu'); } catch { /* egal */ }
  }, [chatOffen]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100dvh', background: 'var(--tn-bg)' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14, padding: '10px 16px',
        background: 'var(--tn-bg-dark)', borderBottom: '1px solid var(--tn-border)', flexShrink: 0,
        flexWrap: 'wrap',
      }}>
        <span style={{ fontSize: 15, fontWeight: 700, fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
          <span style={{ color: 'var(--tnf-f4f6fa)' }}>Werk</span><span style={{ color: 'var(--tnf-d2a845)' }}>ING</span><span style={{ color: 'var(--tnf-9aa7b8)', fontWeight: 500 }}> Partner</span>
        </span>
        <span style={{ fontSize: 12, color: 'var(--tn-text-muted)' }}>Willkommen, {user?.name}</span>
        <div style={{ flex: 1 }} />
        <nav style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          {SECTIONS.map(s => {
            const badge = s.value === 'nachrichten' ? counts.inbox : 0;
            return (
              <button
                key={s.value}
                data-ai-id={`sales-landing-section-${s.value}`}
                onClick={() => setSection(s.value)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '7px 14px', borderRadius: 5, fontSize: 13, cursor: 'pointer',
                  fontWeight: section === s.value ? 700 : 500,
                  background: section === s.value ? 'var(--tn-blue)' : 'var(--tn-surface)',
                  color: section === s.value ? '#fff' : 'var(--tn-text)',
                  border: '1px solid var(--tn-border)',
                }}
              >
                {s.label}
                {badge > 0 && (
                  <span
                    data-ai-id="sales-landing-unread-badge"
                    style={{
                      minWidth: 17, height: 17, borderRadius: 9, padding: '0 5px',
                      background: 'var(--tnfh-f7768e)', color: '#fff', fontSize: 11, fontWeight: 700,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    {badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
        <button
          data-ai-id="sales-landing-toggle-chat"
          onClick={() => setChatOffen(v => !v)}
          title={chatOffen ? 'Chat zuklappen' : 'Chat aufklappen'}
          style={{
            padding: '7px 14px', borderRadius: 5, fontSize: 13, cursor: 'pointer', fontWeight: 600,
            background: chatOffen ? 'var(--tn-surface)' : 'var(--tn-blue)',
            color: chatOffen ? 'var(--tn-text)' : '#fff', border: '1px solid var(--tn-border)',
          }}
        >
          {chatOffen ? 'Chat ▸' : '◂ Chat'}
        </button>
        <button
          data-ai-id="sales-landing-go-to-workspace"
          onClick={() => pickArea('work')}
          style={{
            padding: '7px 12px', borderRadius: 5, fontSize: 13, cursor: 'pointer',
            background: 'transparent', color: 'var(--tn-text-muted)', border: '1px solid var(--tn-border)',
          }}
        >
          → Arbeitsbereich
        </button>
        <StageSharesInbox />
        <button
          data-ai-id="sales-landing-was-wirkt"
          onClick={() => setWasWirktOffen(true)}
          title="Alle Anweisungen, die deine Berater lesen; deine persönliche Anleitung bearbeiten"
          style={{
            padding: '7px 12px', borderRadius: 5, fontSize: 13, cursor: 'pointer',
            background: 'transparent', color: 'var(--tn-text-muted)', border: '1px solid var(--tn-border)',
          }}
        >
          Was wirkt auf mich
        </button>
        {wasWirktOffen && <WasWirktAufMich onClose={() => setWasWirktOffen(false)} />}
        <button
          data-ai-id="sales-landing-logout"
          onClick={() => logout()}
          style={{
            padding: '7px 12px', borderRadius: 5, fontSize: 13, cursor: 'pointer',
            background: 'transparent', color: 'var(--tn-text-muted)', border: '1px solid var(--tn-border)',
          }}
        >
          Abmelden
        </button>
      </div>

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <div style={{ flex: 1, overflow: 'hidden', minWidth: 0 }}>
          <ErrorBoundary>
            <Suspense fallback={<div style={{ padding: 20, color: 'var(--tn-text-muted)' }}>Lädt...</div>}>
              {section === 'heute' && <SalesHeutePanel onWechsel={setSection} />}
              {section === 'ziele' && <SalesZielePanel />}
              {section === 'kunden' && <SalesPanel />}
              {section === 'nachrichten' && <SalesMessagesPanel />}
              {section === 'unterlagen' && <SalesMaterialsPanel />}
              {section === 'leitfaden' && <SalesLeitfadenPanel />}
              {section === 'uebersicht' && <BusinessOverviewPanel />}
              {section === 'feedback' && <FeedbackPanel />}
            </Suspense>
          </ErrorBoundary>
        </div>

        {/* Der Chat bleibt montiert, auch wenn er zugeklappt ist: ein laufender
            KI-Lauf darf nicht abbrechen, nur weil jemand die Spalte schliesst. */}
        <div style={{
          width: chatOffen ? 'min(42%, 620px)' : 0,
          minWidth: chatOffen ? 340 : 0,
          borderLeft: chatOffen ? '1px solid var(--tn-border)' : 'none',
          overflow: 'hidden', flexShrink: 0,
        }}>
          <ErrorBoundary>
            <SalesChatPanel />
          </ErrorBoundary>
        </div>
      </div>
    </div>
  );
}
